import { t } from 'i18next';
import { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Input, Select } from '~/components';
import { apiCall, ApiResps } from '~/providers';
import { EnvironmentList, UnitList } from './ClientPanel';

interface FormEditDutProps {
  dutInfo: EnvironmentList | null,
  unitInfo: { UNIT_ID: number, UNIT_NAME: string } | null,
  unitsList: UnitList,
  roomTypes: (ApiResps['/clients/get-roomtypes-list']['rtypes'] & ({
    DUTS_COUNT?: number
    DRIS_COUNT?: number
    schedCol?: string
  })[]),
  environmentCategories: { value: number, name: string}[],
  onSuccess: ({ item, action }: { item: EnvironmentList, action: string | null }) => void,
  onCancel: () => void,
}

export const FormEditDut = ({
  dutInfo,
  unitInfo,
  unitsList,
  roomTypes,
  environmentCategories,
  onSuccess,
  onCancel,
}: Readonly<FormEditDutProps>): JSX.Element => {
  const match = useRouteMatch<{ clientId: string }>();
  const [, stateChanged] = useState({});
  const [state] = useState({
    changed() { stateChanged({}); },
    submitting: false,
    formData: {
      DEV_ID: dutInfo?.DEV_ID ?? null,
      ROOM_NAME: dutInfo?.ROOM_NAME ?? '',
      unit: unitInfo?.UNIT_ID ? unitsList.find((unit) => unit.UNIT_ID === unitInfo.UNIT_ID) ?? null : null,
      roomType: dutInfo?.RTYPE_ID ? roomTypes.find((item) => item.RTYPE_ID === dutInfo.RTYPE_ID) ?? null : null,
      environmentCategory: dutInfo?.CATEGORY_ID ? environmentCategories?.find((item) => item.value === dutInfo.CATEGORY_ID) ?? null : null,
    },
  });

  const { formData } = state;

  function validateCategory() {
    if (!formData.environmentCategory) {
      toast.error(t('erroSalvarAmbienteSemCategoria'));
      return false;
    }
    return true;
  }

  function validateUnit(): boolean {
    if (!formData.unit) {
      toast.error(t('erroSalvarAmbienteSemUnidade'));
      return false;
    }

    return true;
  }

  function validateName(): boolean {
    if (!formData.ROOM_NAME || formData.ROOM_NAME === '') {
      toast.error(t('erroSalvarAmbienteSemNome'));
      return false;
    }

    return true;
  }

  async function addEnvironment(): Promise<string | null> {
    if (!validateUnit()) return null;
    if (!validateCategory()) return null;
    if (!validateName()) return null;

    const response = await apiCall('/environments/add-environment', {
      CLIENT_ID: Number(match.params.clientId),
      ENVIRONMENT_NAME: formData.ROOM_NAME,
      UNIT_ID: formData.unit?.UNIT_ID as number,
      CATEGORY_ID: formData.environmentCategory?.value ?? undefined,
      RTYPE_ID: (formData.roomType?.RTYPE_ID) ?? undefined,
    });

    return response;
  }

  async function confirm() {
    let response: unknown | null = null;
    let action: string | null = null;
    try {
      state.submitting = true;
      state.changed();
      action = 'set';

      const reqData = {
        DEV_ID: formData.DEV_ID,
        UNIT_ID: formData.unit?.UNIT_ID,
        ROOM_NAME: formData.ROOM_NAME ?? null,
        RTYPE_ID: (formData.roomType?.RTYPE_ID) ?? undefined,
        CATEGORY_ID: formData.environmentCategory?.value ?? undefined,
        CLIENT_ID: dutInfo?.CLIENT_ID ?? undefined,
      };

      if (!dutInfo) {
        response = await addEnvironment();
        if (!response) return;
      }

      if (dutInfo?.DEV_ID === '-') {
        if (!validateCategory()) return;
        response = await apiCall('/environments/edit-environment', {
          ENVIRONMENT_ID: dutInfo.ENVIRONMENT_ID,
          ENVIRONMENT_NAME: reqData.ROOM_NAME,
          RTYPE_ID: reqData.RTYPE_ID,
          CATEGORY_ID: reqData.CATEGORY_ID,
          UNIT_ID: reqData.UNIT_ID,
          CLIENT_ID: dutInfo.CLIENT_ID,
        });
      }
      if (dutInfo?.DEV_ID.startsWith('DUT')) {
        if (!validateCategory()) return;
        // @ts-ignore
        response = await apiCall('/dut/set-dut-info', { ...reqData, ENVIRONMENT_ID: dutInfo.ENVIRONMENT_ID || undefined }).then((data) => ({ data }));
      }
      if (dutInfo?.DEV_ID.startsWith('DRI')) {
        await apiCall('/dri/set-dri-info', {
          DRI_ID: reqData.DEV_ID as string,
          UNIT_ID: reqData.UNIT_ID,
        });
        response = await apiCall('/dri/update-dri-vav', {
          VAV_ID: reqData.DEV_ID as string,
          ROOM_NAME: reqData.ROOM_NAME,
          RTYPE_ID: reqData.RTYPE_ID,
        });
      }
    } catch (err) {
      console.log(err);
      toast.error(t('houveErro'));
    }
    state.submitting = false;
    state.changed();
    if (response && action) onSuccess({ item: (response as { data: EnvironmentList }).data, action });
  }

  return (
    <div>
      {unitsList
        && (
        <Select
          options={unitsList}
          propLabel="UNIT_NAME"
          value={formData.unit}
          placeholder={t('unidade')}
          onSelect={(item) => { toast.warn(t('avisoAlterarUnidadeAmbiente')); state.formData.unit = item; state.changed(); }}
        />
        )}
      <Input
        type="text"
        style={{ marginTop: '10px' }}
        value={formData.ROOM_NAME}
        placeholder={t('nomeAmbiente')}
        onChange={(event) => { state.formData.ROOM_NAME = event.target.value; state.changed(); }}
      />
      {(roomTypes.length > 0)
        && (
        <Select
          style={{ marginTop: '10px' }}
          options={roomTypes}
          propLabel="RTYPE_NAME"
          value={formData.roomType}
          placeholder={t('tipoAmbiente')}
          onSelect={(item) => { state.formData.roomType = item; state.changed(); }}
        />
        )}
      {(environmentCategories?.length > 0 && !formData.DEV_ID?.startsWith('DRI')) && (
        <Select
          style={{ marginTop: '10px' }}
          options={environmentCategories}
          propLabel="name"
          value={formData.environmentCategory}
          placeholder={t('categoriaAmbiente')}
          onSelect={(item) => { state.formData.environmentCategory = item; state.changed(); }}
        />
      )}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '30px',
      }}
      >
        <Button style={{ width: '140px' }} onClick={confirm} variant="primary">
          {t('salvar')}
        </Button>
        <Button style={{ width: '140px', margin: '0 20px' }} onClick={onCancel} variant="grey">
          {t('cancelar')}
        </Button>
      </div>
    </div>
  );
};
